<template>
  <div ref="container" :class="$style.emojiInput">
    <textarea
      ref="editor"
      :class="$style.editor"
      :value="value"
      :placeholder="placeholder"
      :spellcheck="spellcheck"
      :dir="dir"
      @input="onInput"
      @focus="onFocus"
      @blur="onBlur"
      @keydown.enter="onEnter"
      @keydown.esc="onEscape"
    ></textarea>

    <button
      ref="btn"
      :class="[$style.emojiButton, { [$style.active]: pickerIsOpened }]"
      @click="toggle"
      @keydown.esc="onEscape"
    >
      <font-awesome-icon :icon="emojiButtonIcon" />
    </button>
  </div>
</template>

<script>
import { ref, toRefs, watch, computed, onMounted } from "@vue/composition-api";
import { createPopup } from "@picmo/popup-picker";
import { faGrin } from "@fortawesome/pro-regular-svg-icons/faGrin";
import { faTimes } from "@fortawesome/pro-solid-svg-icons/faTimes";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

export default {
  props: {
    value: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "",
    },
    spellcheck: {
      type: Boolean,
      default: true,
    },
    dir: {
      type: String,
      default: "auto",
    },
    focus: {
      type: Boolean,
      default: false,
    },
    allowNewLine: {
      type: Boolean,
      default: true,
    },
    pickerOptions: {
      default: () => {
        return {};
      },
    },
    popupOptions: {
      default: () => {
        return {};
      },
    },
  },
  components: {
    FontAwesomeIcon,
  },
  setup(props, { emit }) {
    const {
      focus,
      allowNewLine,
      pickerOptions: customPickerOptions,
      popupOptions: customPopupOptions,
    } = toRefs(props);

    // template refs
    const container = ref(null);
    const editor = ref(null);
    const btn = ref(null);

    let picker;

    const pickerIsOpened = ref(false);

    const emojiButtonIcon = computed(() => {
      return pickerIsOpened.value ? faTimes : faGrin;
    });

    onMounted(() => {
      const pickerOptions = {
        emojiSize: "22px",
        visibleRows: 4,
        emojisPerRow: 10,
        showPreview: false,
        ...customPickerOptions.value,
      };

      const popupOptions = {
        referenceElement: container.value,
        triggerElement: btn.value,
        hideOnEmojiSelect: false,
        showCloseButton: false,
        position: "top-end",
        ...customPopupOptions.value,
      };

      picker = createPopup(pickerOptions, popupOptions);

      picker.addEventListener("emoji:select", onEmojiSelected);
      picker.addEventListener("picker:open", onPickerOpened);
      picker.addEventListener("picker:close", onPickerClosed);

      if (focus.value) {
        focusEditor();
      }
    });

    watch(focus, (value) => {
      if (value) {
        focusEditor();
      }
    });

    function toggle() {
      pickerIsOpened.value = !picker.isOpen;
      picker.toggle();
    }

    function close() {
      pickerIsOpened.value = false;
      picker.close();
    }

    function focusEditor() {
      editor.value.focus();
    }

    function onEmojiSelected(e) {
      focusEditor();
      document.execCommand("insertText", false, e.emoji);
    }

    function onInput(e) {
      emit("input", e.target.value);
    }

    function onPickerOpened() {
      pickerIsOpened.value = true;
      emit("picker-opened");
    }

    function onPickerClosed() {
      pickerIsOpened.value = false;
      emit("picker-closed");
    }

    function onFocus() {
      emit("focus");
    }

    function onBlur() {
      emit("blur");
    }

    function onEnter(e) {
      if (!allowNewLine.value) {
        e.preventDefault();
        emit("enterKey");
      }
    }

    function onEscape() {
      if (picker.options.hideOnEscape) {
        close();
      }
    }

    return {
      container,
      editor,
      btn,
      pickerIsOpened,
      emojiButtonIcon,
      toggle,
      focusEditor,
      onInput,
      onFocus,
      onBlur,
      onEnter,
      onEscape,
    };
  },
};
</script>

<style module lang="less" src="./emoji-input.less"></style>



// WEBPACK FOOTER //
// emoji-input.vue?39eb8c8d